import { Card, TextField } from '@atgfr8/atg-ui-tailwind';
import { LoadItemTypes } from '@common/commonLists';
import { Button, SelectInput } from '@components';
import {
  CheckboxMarkedOutlineIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@atgfr8/icons';
import {
  orderStore,
  PickUpDropOffItem,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { Component, Index, Show, createSignal } from 'solid-js';

import cls from '../classes';

type UpdateButtonProps = {
  onClick: () => void;
  classes: string;
  noUpdate: boolean;
};

type EDICompareChangeItemsProps = {
  stop: LastUsedTopStopModel;
  noUpdate: boolean;
  items: PickUpDropOffItem[];
  index: number;
};

const EDICompareChangeItems = (props: EDICompareChangeItemsProps) => {
  const [openItems, setOpenItems] = createSignal<Record<string, boolean>>({});

  const UpdateButton: Component<UpdateButtonProps> = (props) => {
    return !props.noUpdate ? (
      <div class={`${props.classes} text-right mt-auto mb-[2px]`}>
        <Button
          startIcon={<CheckboxMarkedOutlineIcon class="text-[#ffffff]" />}
          label="UPDATE"
          class="updateRateClass bg-[#388E3C]!"
          variant="contained"
          onClick={props.onClick}
          dataTestId={formatDataTestId('UPDATE', 'button')}
        />
      </div>
    ) : null;
  };

  const handleItemChange = (
    partialVals: Partial<PickUpDropOffItem>,
    idx: number,
  ) => {
    const updatedItems = orderStore.order.loads[0].items?.map((itm, index) => {
      if (index === idx) {
        return { ...itm, ...partialVals };
      }
      return itm;
    });
    updateLoadPropertyAtIndex(
      {
        items: updatedItems,
      },
      0,
    );
  };

  const handleItemUpdateButtonClick = (
    item: Partial<PickUpDropOffItem>,
    idx: number,
  ) => {
    const storeEditableItems =
      orderStore.order.loads[0].items?.filter(
        (item) => item.operationType !== 'Delete',
      ) ?? [];
    if (storeEditableItems.length > 0) {
      Boolean(storeEditableItems[idx])
        ? (storeEditableItems[idx] = { ...storeEditableItems[idx], ...item })
        : null;
      const updatedItems = orderStore.order.loads[0].items?.map((item) => {
        if (item.id === storeEditableItems[idx].id) {
          return storeEditableItems[idx];
        }
        return item;
      });
      updateLoadPropertyAtIndex(
        {
          items: updatedItems,
        },
        0,
      );
    }
  };

  return (
    <Card class="bg-white! mb-2">
      <div class={cls.currentChangesSubHeading}>Items</div>
      <div class="p-4">
        <Index each={props.items}>
          {(rowData, index) => {
            const itemKey = () => `${props.stop.id}-${rowData().id}`;

            return rowData().operationType !== 'Delete' &&
              (rowData().pickUpId == props.stop.id ||
                rowData().dropOffId == props.stop.id) ? (
              <div class="mb-2">
                <button
                  type="button"
                  class="flex w-full cursor-pointer items-center justify-between py-2 text-left"
                  aria-expanded={openItems()[itemKey()]}
                  aria-controls={`${itemKey()}-content`}
                  onClick={() => {
                    setOpenItems((prev) => ({
                      ...prev,
                      [itemKey()]: !prev[itemKey()],
                    }));
                  }}
                >
                  <span>{`${rowData().quantity} ${rowData().item} ${rowData().description}`}</span>
                  <span aria-hidden="true">
                    {openItems()[itemKey()] ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </span>
                </button>
                <Show when={openItems()[itemKey()]}>
                  <div
                    id={`${itemKey()}-content`}
                    class="flex mb-4 px-1 w-[70%]"
                  >
                    <TextField
                      type="number"
                      label="Quantity"
                      value={rowData().quantity}
                      onChange={(itm: unknown) =>
                        handleItemChange({ quantity: itm as number }, index)
                      }
                      disabled={!props.noUpdate}
                      dataTestId="quantity-textInput"
                    />
                    <UpdateButton
                      onClick={() => {
                        handleItemUpdateButtonClick(
                          {
                            quantity: rowData().quantity,
                          },
                          index,
                        );
                      }}
                      classes="w-[30%]"
                      noUpdate={props.noUpdate || props.index != 0}
                    />
                  </div>
                  <div class="flex mb-4 px-1 w-[70%]">
                    <SelectInput
                      label=""
                      menuItems={LoadItemTypes.map((t) => ({
                        label: t,
                        value: t,
                      }))}
                      value={rowData().item}
                      onChange={(val) => {
                        handleItemChange({ item: val }, index);
                      }}
                      disabled={!props.noUpdate}
                      dataTestId={formatDataTestId(
                        'EDI compare item type',
                        'selectInput',
                      )}
                    />
                    <UpdateButton
                      onClick={() => {
                        handleItemUpdateButtonClick(
                          {
                            item: rowData().item,
                          },
                          index,
                        );
                      }}
                      classes="w-[30%]"
                      noUpdate={props.noUpdate || props.index != 0}
                    />
                  </div>
                  <div class="flex mb-4 px-1 w-[70%]">
                    <TextField
                      label="Description"
                      value={rowData().description}
                      onChange={(itm: string) =>
                        handleItemChange({ description: itm }, index)
                      }
                      disabled={!props.noUpdate}
                      dataTestId="description-textInput"
                    />
                    <UpdateButton
                      onClick={() => {
                        handleItemUpdateButtonClick(
                          {
                            description: rowData().description,
                          },
                          index,
                        );
                      }}
                      classes="w-[30%]"
                      noUpdate={props.noUpdate || props.index != 0}
                    />
                  </div>
                  <div class="flex mb-4 px-1 w-[70%]">
                    <TextField
                      label="Weight"
                      value={rowData().weight}
                      onChange={(itm: number | string) =>
                        handleItemChange({ weight: itm as number }, index)
                      }
                      disabled={!props.noUpdate}
                      dataTestId="weight-textInput"
                    />
                    <UpdateButton
                      onClick={() => {
                        handleItemUpdateButtonClick(
                          {
                            weight: rowData().weight,
                          },
                          index,
                        );
                      }}
                      classes="w-[30%]"
                      noUpdate={props.noUpdate || props.index != 0}
                    />
                  </div>
                </Show>
              </div>
            ) : null;
          }}
        </Index>
      </div>
    </Card>
  );
};

export default EDICompareChangeItems;
